import type { KeyboardShortcut } from '@unifyapps/defs/blocks/common/keyboardShortcut';
import type { EmitActionParams } from '@unifyapps/defs/hooks/useBlockAction';
import type { Event, OnBlockVisibilityChangePayload } from '@unifyapps/defs/types/event';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import type { SyntheticEvent } from '@unifyapps/defs/types/syntheticEvent';
import type { Notification as NotificationType } from '@unifyapps/network/generated/models/notification';
import { EMPTY_ARRAY } from '../../../consts/empty';
import { isKeyboardShortcutEvent } from '../../../utils/keyboardShortcut';

export function getEventsToTrigger({
  events,
  actionContext,
  eventType,
  domEvent,
  eventPayload,
}: {
  events: Event[];
  eventType: string;
  actionContext?: EmitActionParams['actionContext'];
  domEvent?: SyntheticEvent;
  eventPayload?: Record<string, unknown>;
}) {
  let filteredEvents: Event[];

  switch (eventType) {
    case EventTypeEnum.OnReceiveNotification: {
      filteredEvents = events.filter(
        (event) =>
          event.payload?.notificationType &&
          event.payload.notificationType ===
            (actionContext as { notification?: NotificationType } | undefined)?.notification?.id,
      );
      break;
    }
    case EventTypeEnum.OnKeyboardShortcut: {
      if (!domEvent) return EMPTY_ARRAY as Event[];

      filteredEvents = events.filter(
        (event) =>
          event.payload?.shortcut &&
          isKeyboardShortcutEvent(
            domEvent as KeyboardEvent,
            event.payload.shortcut as KeyboardShortcut,
          ),
      );
      break;
    }
    case EventTypeEnum.OnReceiveMQTTEvent: {
      filteredEvents = events.filter(
        (event) =>
          event.payload?.topicId &&
          event.payload.topicId === (actionContext as { topicId?: string } | undefined)?.topicId,
      );
      break;
    }
    case EventTypeEnum.OnScrollVisibilityChange: {
      // dispatch only when the emitted (targetBlockId, visibilityState) matches the
      // registered event's payload — the Scrollable reports reality, this filters to intent.
      const emitted = eventPayload as Partial<OnBlockVisibilityChangePayload> | undefined;
      if (!emitted?.targetBlockId || !emitted.visibilityState) {
        return EMPTY_ARRAY as Event[];
      }
      filteredEvents = events.filter((event) => {
        const registered = event.payload as Partial<OnBlockVisibilityChangePayload> | undefined;
        return (
          registered?.targetBlockId === emitted.targetBlockId &&
          registered?.visibilityState === emitted.visibilityState
        );
      });
      break;
    }
    default: {
      filteredEvents = events;
    }
  }

  // filter out disabled events
  return filteredEvents.filter((event) => !event.disabled);
}
